import { useEffect, useState } from 'react';
import { Icon } from '../../components/icons/Icon';
import { Button, Modal, useToast } from '../../components/primitives';
import { cx } from '../../lib/cx';
import { AccountSettingsModal } from './AccountSettingsModal';
import { MojeFirmaQRModal } from './MojeFirmaQRModal';
import {
  copyToClipboard,
  firmaRows,
  firmaText,
  loadLastFirmaId,
  saveLastFirmaId,
  useMojeFirmy,
} from './mojeFirmy';
import styles from './Ucet.module.css';

const MONO_LABELS = new Set(['IČO', 'DIČ', 'Číslo účtu', 'IBAN', 'SWIFT']);

/**
 * 🏢 Moje údaje — tlačítko v hlavičce. Otevře vlastní fakturační údaje
 * (víc firem, přepínání záložkami), klik na řádek zkopíruje hodnotu,
 * „Kopírovat vše" celý blok. Úprava přes Nastavení účtu.
 */
export function MojeUdajeButton() {
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(() => loadLastFirmaId());
  const toast = useToast();
  const firmyQuery = useMojeFirmy(open);
  const firmy = firmyQuery.data ?? [];
  const active = firmy.find((f) => f.id === activeId) ?? firmy[0];

  useEffect(() => {
    if (active && active.id !== activeId) setActiveId(active.id);
  }, [active, activeId]);

  function pick(id: number) {
    setActiveId(id);
    saveLastFirmaId(id);
  }

  async function copy(text: string, what: string) {
    try {
      await copyToClipboard(text);
      toast.success(`Zkopírováno: ${what}`, 2000);
    } catch {
      toast.error('Kopírování se nepodařilo — označte text ručně.');
    }
  }

  function openSettings() {
    setOpen(false);
    setSettingsOpen(true);
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        leading={<Icon name="building" />}
        onClick={() => setOpen(true)}
        title="Moje fakturační údaje — rychlé kopírování"
      >
        <span className="sd-phone-hide">Moje údaje</span>
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="🏢 Moje fakturační údaje"
        footer={
          active ? (
            <>
              <Button onClick={openSettings}>✎ Upravit</Button>
              <Button onClick={() => setQrOpen(true)} title="QR kód k uložení kontaktu do telefonu">
                📱 QR vizitka
              </Button>
              <Button variant="primary" onClick={() => void copy(firmaText(active), 'všechny údaje')}>
                📋 Kopírovat vše
              </Button>
            </>
          ) : undefined
        }
      >
        {firmyQuery.isLoading ? (
          <div className={styles.muted}>Načítám…</div>
        ) : firmyQuery.isError ? (
          <div className={styles.error}>Údaje se nepodařilo načíst.</div>
        ) : !active ? (
          <div className={styles.empty}>
            Zatím nemáte vyplněné žádné fakturační údaje. Doplňte své firmy (IČO se načte z ARES) a pak je
            tady jedním klikem zkopírujete.
            <Button variant="primary" onClick={openSettings}>
              + Vyplnit údaje firmy
            </Button>
          </div>
        ) : (
          <div className={styles.section}>
            {firmy.length > 1 && (
              <div className={styles.pills} role="tablist" aria-label="Moje firmy">
                {firmy.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    role="tab"
                    aria-selected={f.id === active.id}
                    className={cx(styles.pill, f.id === active.id && styles.pillActive)}
                    onClick={() => pick(f.id)}
                    title={f.nazev}
                  >
                    {f.nazev}
                  </button>
                ))}
              </div>
            )}
            <div className={styles.card}>
              {firmaRows(active).map((r) => (
                <button
                  key={r.label}
                  type="button"
                  className={styles.dataRow}
                  onClick={() => void copy(r.value, r.label)}
                  title={`Kopírovat ${r.label}`}
                >
                  <span className={styles.dataLabel}>{r.label}</span>
                  <span className={cx(styles.dataValue, MONO_LABELS.has(r.label) && styles.mono)}>{r.value}</span>
                  <span className={styles.copyHint} aria-hidden>
                    📋
                  </span>
                </button>
              ))}
            </div>
            <div className={styles.muted}>Kliknutím na řádek zkopírujete jednu hodnotu.</div>
          </div>
        )}
      </Modal>

      <MojeFirmaQRModal firma={active} open={qrOpen} onClose={() => setQrOpen(false)} />

      <AccountSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        initialFirmaId={active?.id ?? null}
      />
    </>
  );
}
